<template>
	<div>
		<!-- Snackbar -->
		<Snackbar
			@close-snackbar="snackbar.status = false"
			v-model="snackbar.status"
			:text="snackbar.text"
			:color="snackbar.color"
		/>

		<Breadcrumbs title="Dashboard" />

		<!-------------------------------------------------------------------------------
        | Dropdown
        |-------------------------------------------------------------------------------->
		<!-- Substance -->
		<v-row>
			<v-col cols="12" sm>
				<Autocomplete
					label="Substance"
					:items="substances"
					v-model="selected.substances"
					:loading="isLoading.substances"
					:disabled="isLoading.market"
				/>
			</v-col>

			<!-- Product name -->
			<v-col cols="12" sm>
				<Autocomplete
					label="Product name"
					:items="product_names"
					v-model="selected.product_names"
					:loading="isLoading.product_names"
					:disabled="isLoading.market"
				/>
			</v-col>

			<!-- Form -->
			<v-col cols="12" sm>
				<Autocomplete
					label="Form"
					:items="forms"
					v-model="selected.forms"
					:loading="isLoading.forms"
					:disabled="isLoading.market"
				/>
			</v-col>

			<!-- Strength -->
			<v-col cols="12" sm>
				<Autocomplete
					label="Strength"
					:items="strengths"
					v-model="selected.strengths"
					:loading="isLoading.strengths"
					:disabled="isLoading.market"
				/>
			</v-col>

			<!-- Submit button -->
			<v-col cols="12" sm="1" class="search-btn">
				<v-btn class="primary submit" block @click="submit" :loading="isLoading.market">
					<v-icon v-if="$vuetify.breakpoint.smAndUp">mdi-magnify</v-icon>
					<div v-else>
						<span>Search</span>
						<v-icon right>mdi-magnify</v-icon>
					</div>
				</v-btn>
			</v-col>
		</v-row>

		<!-------------------------------------------------------------------------------
        | Market overview charts
        |-------------------------------------------------------------------------------->
		<!-- Market share Hospital MAT -->
		<v-row>
			<v-col cols="12" md="6" v-if="market.chartData.hos.length">
				<v-card class="card-ms" flat>
					<v-card-title> Market Share Hospital MAT </v-card-title>
					<v-card-text>
						<div ref="chart_div1"></div>
					</v-card-text>
				</v-card>
			</v-col>

			<!-- Market share Pharmacy MAT -->
			<v-col cols="12" md="6" v-if="market.chartData.pha.length">
				<v-card class="card-ms" flat>
					<v-card-title> Market Share Pharmacy MAT </v-card-title>
					<v-card-text>
						<div ref="chart_div2"></div>
					</v-card-text>
				</v-card>
			</v-col>
		</v-row>

		<v-row v-if="market.articles.length">
			<v-col>
				<v-card flat>
					<v-card-title>Market overview</v-card-title>

					<v-data-table
						:headers="market.headers"
						:items="market.articles"
						:items-per-page="10"
						:sort-by="sortBy"
						:sort-desc="sortDesc"
					>
						<template v-slot:item="{ item }">
							<tr>
								<td>{{ item.substance }}</td>
								<td>{{ item.product_name }}</td>
								<td>{{ item.form }}</td>
								<td>{{ item.strength }}</td>
								<td>{{ item.size }}</td>
								<td>{{ handleRow(item.value_mat) }}</td>
								<td>{{ handleRow(item.ms_value_mat, true) }}</td>
								<td>{{ handleRow(item.value_qty, false, true) }}</td>
								<td>{{ handleRow(item.ms_value_qty, true) }}</td>
							</tr>
						</template>
						<template slot="body.append">
							<tr class="grey lighten-3">
								<th></th>
								<th></th>
								<th></th>
								<th></th>
								<th><h3>Total</h3></th>
								<th>{{ handleTotal(market.articles, 'value_mat', 'kr') }}</th>
								<th>{{ handleTotal(market.articles, 'ms_value_mat', '%') }}</th>
								<th>{{ handleTotal(market.articles, 'value_qty') }}</th>
								<th>{{ handleTotal(market.articles, 'ms_value_qty', '%') }}</th>
							</tr>
						</template>
					</v-data-table>
				</v-card>
			</v-col>
		</v-row>
	</div>
</template>

<script>
	import _ from 'lodash';
	import utils from '@/utils.js';
	import { eventBus } from '@/eventBus.js';
	import DashboardAPI from '@/api/dashboard.js';
	import Snackbar from '@/components/Snackbar.vue';
	import PieChart from '@/components/PieChart.vue';
	import Breadcrumbs from '@/components/Breadcrumbs.vue';
	import Autocomplete from '@/components/Autocomplete.vue';

	google.charts.load('current', { packages: ['corechart'] });

	/**
	 * @module GenericDashboardView
	 *
	 * @vue-data {object} [isLoading={}] - Display progress on buttons or forms
	 * @vue-data {boolean} [isLoading.product_names=false] - Display progress for product names
	 * @vue-data {boolean} [isLoading.substances=false] - Display progress for substances
	 * @vue-data {boolean} [isLoading.forms=false] - Display progress for forms
	 * @vue-data {boolean} [isLoading.strengths=false] - Display progress for strengths
	 * @vue-data {object} [snackbar={}] - Display message to user
	 * @vue-data {boolean} [snackbar.status=false] - Control whether to show/hide the snackbar 
	 * @vue-data {string} [snackbar.text=''] - Sets the snackbar message  
	 * @vue-data {string} [snackbar.color=''] - Sets the color of the snackbar
	 * @vue-data {string} [sortBy=value_mat] - Sort market overview table by 'Value MAT'
	 * @vue-data {boolean} [sortDesc=true] - Sort market overview table in descending order for column 'Value MAT'
	 * @vue-data {array<string>} [product_names=[]] - Product names to store in dropdown
	 * @vue-data {array<string>} [substances=[]] - Substances to store in dropdown
	 * @vue-data {array<string>} [forms=[]] - Forms to store in dropdown
	 * @vue-data {array<string>} [strengths=[]] - Strengths to store in dropdown
	 * @vue-data {object} [selected={}] - Store selected input values 
	 * @vue-data {array} [selected.product_names=[]] - Selected product names
	 * @vue-data {array} [selected.substances=[]] - Selected substances
	 * @vue-data {array} [selected.forms=[]] - Selected forms
	 * @vue-data {array} [selected.strengths=[]] - Selected strengths
	 * @vue-data {object} [market={}] - Table data to be displayed such as chart data, articles and specified column headers
	 * @vue-data {object} [market.chartData={}] - Store market chart data
	 * @vue-data {array<object>} [market.chartData.hos=[]] - Chart data for Hospital
	 * @vue-data {array<object>} [market.chartData.pha=[]] - Chart data for Pharmacy
	 * @vue-data {array<object>} [market.articles=[]] - Market articles data 
	 * @vue-data {array<object>} [market.headers=[{ text: 'Substance', value: 'substance' }, { text: 'Product name', value: 'product_name' }, { text: 'Form', value: 'form' }, { text: 'Strength', value: 'strength' }, { text: 'Pack size', value: 'size' }, { text: 'Value MAT', value: 'value_mat' }, { text: 'MS Value MAT %', value: 'ms_value_mat' }, { text: 'QTY MAT', value: 'value_qty' }, { text: 'MS QTY MAT %', value: 'ms_value_qty' }]] - Table column headers 
	 */

	export default {
		name: 'GenericDashboard',
		components: {
			Snackbar,
			Breadcrumbs,
			Autocomplete,
			PieChart,
		},
		data: () => ({
			isLoading: {
				product_names: false,
				substances: false,
				forms: false,
				strengths: false,
				market: false,
			},

			snackbar: {
				status: false,
				text: '',
				color: '',
			},
			sortBy: 'value_mat',
			sortDesc: true,

			// Dropdown
			product_names: [],
			substances: [],
			forms: [],
			strengths: [],

			selected: {
				product_names: [],
				substances: [],
				forms: [],
				strengths: [],
			},

			market: {
				chartData: {
					hos: [],
					pha: [],
				},
				articles: [],
				headers: [
					{ text: 'Substance', value: 'substance' },
					{ text: 'Product name', value: 'product_name' },
					{ text: 'Form', value: 'form' },
					{ text: 'Strength', value: 'strength' },
					{ text: 'Pack size', value: 'size' },
					{ text: 'Value MAT', value: 'value_mat' },
					{ text: 'MS Value MAT %', value: 'ms_value_mat' },
					{ text: 'QTY MAT', value: 'value_qty' },
					{ text: 'MS QTY MAT %', value: 'ms_value_qty' },
				],
			},
		}),

		// Load data for dropdowns when Vue instance gets created
		created() {
			this.getProductNames();
			this.getSubstances();
			this.getForms();
			this.getStrengths();
		},

		// Add event listener to redraw the charts when window gets resized
		mounted() {
			window.addEventListener('resize', this.drawHospitalChart);
			window.addEventListener('resize', this.drawPharmacyChart);
		},

		// Remove event listener before Vue instance is destroyed
		beforeDestroy() {
			window.removeEventListener('resize', this.drawHospitalChart);
			window.removeEventListener('resize', this.drawPharmacyChart);
		},

		/**
		 * Listen for value changes in selected.product_names, selected.substances, selected.form, selected.strength
		 *
		 * @property {function} selected.product_names - Filter substances, forms, and strengths based on selected product name
		 * @property {function} selected.substances - Filter product_names, forms, and strengths based on selected substance
		 * @property {function} selected.form - Filter substances, product_names and strengths based on selected form
		 * @property {function} selected.strength - Filter substances, forms, and product_names based on selected strength
		 */
		watch: {
			'selected.product_names': function() {
				this.getSubstances();
				this.getForms();
				this.getStrengths();
			},
			'selected.substances': function() {
				this.getProductNames();
				this.getForms();
				this.getStrengths();
			},
			'selected.forms': function() {
				this.getProductNames();
				this.getSubstances();
				this.getStrengths();
			},
			'selected.strengths': function() {
				this.getProductNames();
				this.getSubstances();
				this.getForms();
			},
		},

		methods: {
			/**
			 * Format total rows
			 *
			 * @param {array<object>} key - Articles that we want to format
			 * @param {string} value - Key we want to format
			 * @param {string|undefined} param - If row should have 'kr' or '%' at the end
			 *
			 * @returns {string} - Formatted total row
			 */
			handleTotal(key, value, param) {
				if (key.length) {
					if (param === 'kr') {
						const totalSEK = key
							.reduce((a, b) => a + b[value] || 0, 0)
							.toLocaleString('sv-SE', {
								maximumFractionDigits: 0,
								minimumFractionDigits: 0,
							});

						return totalSEK === 0 ? '-' : totalSEK + ' kr';
					} else if (param === '%') {
						const sum = key.reduce((a, b) => a + b[value] || 0, 0);
						const totalPercent = new Intl.NumberFormat('sv-SE', {
							minimumFractionDigits: 0,
							maximumFractionDigits: 0,
						}).format(sum);

						return totalPercent === 0 ? '-' : totalPercent + ' %';
					} else {
						const sum = key.reduce((a, b) => a + b[value] || 0, 0);
						const total = new Intl.NumberFormat('sv-SE', {
							minimumFractionDigits: 0,
							maximumFractionDigits: 0,
						}).format(sum);

						return total === 0 ? '-' : total;
					}
				}
			},
            
			/**
			 * Format rows
			 *
			 * @param {number} item - Item to format
			 * @param {boolean} percent - Is percent
			 * @param {boolean} qty - Is qty
			 *
			 * @returns {number} - Formatted row
			 */
			handleRow(item, percent = false, qty = false) {
				if (item === 0 || item === null || isNaN(item)) {
					return '-';
				} else if (percent) {
					return Number.parseFloat(item).toFixed(2) + ' %';
				} else if (qty) {
					return new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 }).format(item);
				} else {
					return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(item);
				}
			},

			/**
			 * @returns {void}
			 *
			 * @description
			 * Submit form to get market data
			 *
			 * - If no value is selected, display snackbar and hide it after 4s
			 * - Else, close the sidebar and display market data based on selected values
			 */
			submit() {
				if (utils.arraysInObjectIsEmpty(this.selected)) {
					this.snackbar = {
						status: true,
						text: 'Please select a value to start searching!',
						color: 'primary',
					};

					setTimeout(() => {
						this.snackbar.status = false;
					}, 4000);
				} else {
					eventBus.$emit('close-sidebar');
					this.getMarket();
				}
			},

			/*
            |-------------------------------------------------------------------------------
            | Google charts 
            |-------------------------------------------------------------------------------
            */

			/**
			 * @returns {void}
			 *
			 * @description
			 * Draws the chart for Hospital data
			 *
			 * - Format chart data
			 * - Create chart data table
			 * - Set chart options
			 * - Format labels
			 * - Draw chart
			 */
			drawHospitalChart() {
				if (this.market.chartData.hos.length) {
					const hospital = Array.from(
						this.market.chartData.hos
							.reduce((m, { name, value }) => m.set(name, (m.get(name) || 0) + value), new Map())
							.entries(),
						([name, value]) => [name, parseFloat(value.toFixed(2))]
					);

					// Create the data table.
					const data = new google.visualization.DataTable();
					data.addColumn('string', 'Key');
					data.addColumn('number', 'Value');
					data.addRows(hospital);

					// Set chart options
					const options = {
						width: '100%',
						height: 350,
						sliceVisibilityThreshold: 0.01,
						legend: { position: 'right', alignment: 'center' },
						chartArea: {
							right: 20,
							left: 10,
						},
						theme: 'maximized',
					};

					// Format labels
					const formatter = new google.visualization.NumberFormat({
						groupingSymbol: ' ',
						fractionDigits: 0,
						suffix: ' SEK',
					});
					formatter.format(data, 1);

					// Instantiate and draw our chart, passing in some options.
					const chart = new google.visualization.PieChart(this.$refs.chart_div1);
					chart.draw(data, options);
				}
			},

			/**
			 * @returns {void}
			 *
			 * @description
			 * Draws the chart for Pharmacy data
			 *
			 * - Format chart data
			 * - Create chart data table
			 * - Set chart options
			 * - Format labels
			 * - Draw chart
			 */
			drawPharmacyChart() {
				if (this.market.chartData.pha.length) {
					const pharmacy = Array.from(
						this.market.chartData.pha
							.reduce((m, { name, value }) => m.set(name, (m.get(name) || 0) + value), new Map())
							.entries(),
						([name, value]) => [name, parseFloat(value.toFixed(2))]
					);

					// Create the data table.
					const data = new google.visualization.DataTable();
					data.addColumn('string', 'Key');
					data.addColumn('number', 'Value');
					data.addRows(pharmacy);

					// Set chart options
					const options = {
						width: '100%',
						height: 350,
						sliceVisibilityThreshold: 0.01,
						legend: { position: 'right', alignment: 'center' },
						chartArea: {
							right: 20,
							left: 10,
						},
						theme: 'maximized',
					};

					// Format labels
					const formatter = new google.visualization.NumberFormat({
						groupingSymbol: ' ',
						fractionDigits: 0,
						suffix: ' SEK',
					});
					formatter.format(data, 1);

					// Instantiate and draw our chart, passing in some options.
					const chart = new google.visualization.PieChart(this.$refs.chart_div2);
					chart.draw(data, options);
				}
			},

			/*
                GET     /api/v1/market
            */

			/**
			 * @returns {void}
			 *
			 * @description
			 * Get market data and populate datatables/charts, apply filter based on selected value
			 *
			 * - Display progress on dropdown
			 * - Fetch market data
			 * - If no data is found, display it to the user and clear the previous result
			 * <br>&nbsp;
			 * - @HTTP 200
			 * - Populate datatables and charts
			 * <br>&nbsp;
			 * - @HTTP 400/500
			 * - Display error message
			 */
			async getMarket() {
				this.isLoading.market = true;

				try {
					const { data } = await DashboardAPI.market(this.selected);

					if (utils.arraysInObjectIsEmpty(data.article_data)) {
						this.snackbar = {
							status: true,
							text: 'No results found.',
							color: 'error',
						};

						// Clear results
						this.market.chartData.hos = [];
						this.market.chartData.pha = [];
						this.market.articles = [];
					} else {
						this.market.chartData.hos = data.chart_data.hos ?? [];
						this.market.chartData.pha = data.chart_data.pha ?? [];
						this.market.articles = data.article_data;

						google.charts.setOnLoadCallback(() => this.drawHospitalChart());
						google.charts.setOnLoadCallback(() => this.drawPharmacyChart());
					}
				} catch (error) {
					console.error(error);

					if (error) {
						this.snackbar = {
							status: true,
							text: 'Oops, something went wrong!',
							color: 'error',
						};
					}
				} finally {
					this.isLoading.market = false;

					setTimeout(() => {
						this.snackbar.status = false;
					}, 4000);
				}
			},

			/*  
                GET     /api/v1/substances
            */

			/**
			 * @returns {void}
			 *
			 * @description
			 * Load substances in dropdown based on selected product name, form, strength
			 *
			 * - Display progress on substances dropdown
			 * - Fetch substances based on selected values
			 */
			getSubstances() {
				this.isLoading.substances = true;
				DashboardAPI.substances(this.selected)
					.then(response => {
						if (this.selected.substances.length) {
							this.substances = this.selected.substances.concat(response.data);
						} else {
							this.substances = response.data;
						}
					})
					.catch(error => console.error(error))
					.finally(() => (this.isLoading.substances = false));
			},

			/*
                GET 	/api/v1/product_names
            */

			/**
			 * @returns {void}
			 *
			 * @description
			 * Load product names in dropdown based on selected substance, form, strength
			 *
			 * - Display progress on product names dropdown
			 * - Fetch product names based on selected values
			 */
			getProductNames() {
				this.isLoading.product_names = true;
				DashboardAPI.productNames(this.selected)
					.then(response => {
						if (this.selected.product_names.length) {
							this.product_names = this.selected.product_names.concat(response.data);
						} else {
							this.product_names = response.data;
						}
					})
					.catch(error => console.error(error))
					.finally(() => (this.isLoading.product_names = false));
			},

			/*
                GET 	/api/v1/forms
            */

			/**
			 * @returns {void}
			 *
			 * @description
			 * Load forms in dropdown based on selected substance, prodcut name, strength
			 *
			 * - Display progress on forms dropdown
			 * - Fetch forms based on selected values
			 */
			getForms() {
				this.isLoading.forms = true;
				DashboardAPI.forms(this.selected)
					.then(response => {
						if (this.selected.forms.length) {
							this.forms = this.selected.forms.concat(response.data);
						} else {
							this.forms = response.data;
						}
					})
					.catch(error => console.error(error))
					.finally(() => (this.isLoading.forms = false));
			},

			/*
                GET 	/api/v1/strengths
            */

			/**
			 * @returns {void}
			 *
			 * @description
			 * Load strengths in dropdown based on selected substance, prodcut name, form
			 *
			 * - Display progress on strengths dropdown
			 * - Fetch strengths based on selected values
			 */
			getStrengths() {
				this.isLoading.strengths = true;
				DashboardAPI.strengths(this.selected)
					.then(response => {
						if (this.selected.strengths.length) {
							this.strengths = this.selected.strengths.concat(response.data);
						} else {
							this.strengths = response.data;
						}
					})
					.catch(error => console.error(error))
					.finally(() => (this.isLoading.strengths = false));
			},
		},
	};
</script>

<style scoped>
	.search-btn {
		max-width: 100% !important;
	}
</style>
